import axios, { AxiosError } from "axios";
import { getErrorCode, getRequestUrl } from "helpers";
import { createLogger } from "logging";
import { environment } from "models/Environment";
import { useAuthentication } from "providers/Authentication/useAuthentication";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useMemo } from "react";
import { BoardApi } from "services/BoardApi";
import { BoardRoleApi } from "services/BoardRoleApi";
import { DownloadApi } from "services/DownloadApi";
import { ReportApi } from "services/ReportApi";
import { RunApi } from "services/RunApi";
import { RunParticipantApi } from "services/RunParticipantApi";
import { RunTopicApi } from "services/RunTopicApi";
import { UserApi } from "services/UserApi";
import { VotingApi } from "services/VotingApi";

import { ApiContext, IApiContext } from "./ApiContext";

const logger = createLogger("ApiProvider");

export function ApiProvider({ children }: PropsWithChildren<unknown>) {
  const i18n = useTranslations();
  const { acquireTokenSilent } = useAuthentication();
  const acceptLanguage = `${i18n.currentLanguage || "de"}-CH`;

  const axiosInst = useMemo(() => {
    const axiosInst = axios.create();

    const interceptors = axiosInst.interceptors;
    interceptors.request.use(
      async (request) => {
        const accessToken = await acquireTokenSilent();
        request.headers.authorization = `Bearer ${accessToken}`;
        request.headers[`Accept-Language`] = acceptLanguage;
        request.headers[`Pragma`] = "no-cache";

        const url = request.url || "";
        if (!url.startsWith("http")) {
          request.baseURL = `${environment.backendUrl}/api`;
        }

        logger.trace("request(): ", request);
        return request;
      },
      (error) => {
        logger.error("request(): ", error);
        return Promise.reject(error);
      }
    );

    interceptors.response.use(
      (response) => {
        logger.trace("response(): success", response);
        return response;
      },

      async (error: AxiosError) => {
        const errorCode = getErrorCode(error);
        const requestUrl = getRequestUrl(error);
        logger.debug("response(): error", requestUrl, errorCode, error);

        if (errorCode === 401 || errorCode === 403) {
          logger.warn(`Received ${errorCode} from API, triggering re-authentication`);
          try {
            await acquireTokenSilent();
          } catch {
            // acquireTokenSilent will handle redirect if needed
          }
        }

        return Promise.reject(error);
      }
    );

    return axiosInst;
  }, [acquireTokenSilent, acceptLanguage]);

  const value = useMemo<IApiContext>(() => {
    const downloadApi = new DownloadApi(axiosInst);
    const boardApi = new BoardApi(axiosInst, downloadApi);
    const boardRoleApi = new BoardRoleApi(axiosInst);
    const reportApi = new ReportApi(axiosInst, downloadApi);
    const runApi = new RunApi(axiosInst);
    const runParticipantApi = new RunParticipantApi(axiosInst);
    const runTopicApi = new RunTopicApi(axiosInst);
    const userApi = new UserApi(axiosInst);
    const votingApi = new VotingApi(axiosInst);
    return {
      axios: axiosInst,
      boardApi,
      boardRoleApi,
      downloadApi,
      reportApi,
      runApi,
      runParticipantApi,
      runTopicApi,
      userApi,
      votingApi,
    };
  }, [axiosInst]);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}
